import { useState, useEffect, useCallback } from 'react'
import {
  Page,
  Layout,
  Card,
  Button,
  DataTable,
  Badge,
  Text,
  Banner,
  Spinner,
  EmptyState,
  Modal,
  LegacyStack as Stack,
  Tabs,
  ProgressBar,
  ResourceList,
  ResourceItem,
  Thumbnail,
  TextContainer,
  ButtonGroup,
  Select,
  Filters,
  ChoiceList
} from '@shopify/polaris'
import {
  RefreshMajor,
  ExportMinor,
  ViewMinor,
  CircleTickMajor,
  CircleAlertMajor,
  ClockMinor
} from '@shopify/polaris-icons'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

interface CatalogProduct {
  id: string
  productId: string
  title: string
  description: string | null
  price: number
  currency: string
  availability: string
  imageUrl: string | null
  viewCount: number
  clickCount: number
  conversionCount: number
  lastViewedAt: string | null
  syncedAt: string | null
  product?: {
    id: string
    title: string
    handle: string
    images: any
  }
}

interface SyncStatus {
  status: string
  lastSyncedAt: string | null
  statistics?: {
    total: number
    added: number
    updated: number
    removed: number
    failed: number
  }
  message?: string
}

interface SyncHistory {
  id: string
  syncType: string
  status: string
  productsTotal: number
  productsAdded: number
  productsUpdated: number
  productsRemoved: number
  productsFailed: number
  startedAt: string
  completedAt: string | null
  durationMs: number | null
}

export function CatalogManagementPage() {
  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [products, setProducts] = useState<CatalogProduct[]>([])
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null)
  const [syncHistory, setSyncHistory] = useState<SyncHistory[]>([])
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [syncType, setSyncType] = useState<'full' | 'incremental'>('incremental')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [availabilityFilter, setAvailabilityFilter] = useState<string[]>([])
  const [sortBy, setSortBy] = useState('recent')

  const tabs = [
    { id: 'products', content: 'Products', badge: products.length.toString() },
    { id: 'sync', content: 'Sync History' },
    { id: 'analytics', content: 'Analytics' }
  ]

  // Fetch catalog data
  const fetchCatalogData = useCallback(async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')

      // Fetch catalog products
      const catalogsRes = await axios.get('/api/catalog', {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (catalogsRes.data.data.catalogs.length > 0) {
        const catalogId = catalogsRes.data.data.catalogs[0].id

        // Fetch products
        const productsRes = await axios.get(`/api/catalog/${catalogId}/products`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setProducts(productsRes.data.data.products)
      }

      // Fetch sync status
      const statusRes = await axios.get('/api/catalog/sync/latest-status', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSyncStatus(statusRes.data.data)

      // Fetch sync history
      const historyRes = await axios.get('/api/catalog/sync/history', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSyncHistory(historyRes.data.data.history)

      setError(null)
    } catch (err: any) {
      console.error('Failed to fetch catalog data:', err)
      setError(err.response?.data?.message || 'Failed to load catalog data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCatalogData()
  }, [fetchCatalogData])

  // Handle sync
  const handleSync = async () => {
    try {
      setSyncing(true)
      setError(null)
      const token = localStorage.getItem('token')

      const endpoint = syncType === 'full'
        ? '/api/catalog/sync/full'
        : '/api/catalog/sync/incremental'

      await axios.post(
        endpoint,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )

      setSuccess(`${syncType === 'full' ? 'Full' : 'Incremental'} sync initiated successfully`)
      setShowSyncModal(false)

      // Refresh data after 5 seconds
      setTimeout(() => {
        fetchCatalogData()
      }, 5000)
    } catch (err: any) {
      console.error('Sync failed:', err)
      setError(err.response?.data?.message || 'Failed to initiate sync')
    } finally {
      setSyncing(false)
    }
  }

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchQuery === '' ||
      product.title.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesAvailability = availabilityFilter.length === 0 ||
      availabilityFilter.includes(product.availability)

    return matchesSearch && matchesAvailability
  })

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'views':
        return b.viewCount - a.viewCount
      case 'clicks':
        return b.clickCount - a.clickCount
      case 'conversions':
        return b.conversionCount - a.conversionCount
      case 'price-high':
        return b.price - a.price
      case 'price-low':
        return a.price - b.price
      default:
        return new Date(b.syncedAt || 0).getTime() - new Date(a.syncedAt || 0).getTime()
    }
  })

  // Products Tab Content
  const renderProductsTab = () => {
    if (loading) {
      return (
        <Card>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Spinner size="large" />
          </div>
        </Card>
      )
    }

    if (products.length === 0) {
      return (
        <Card>
          <EmptyState
            heading="No products in catalog"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            action={{
              content: 'Sync Products from Shopify',
              onAction: () => setShowSyncModal(true)
            }}
          >
            <p>Sync your Shopify products to make them available in WhatsApp catalog.</p>
          </EmptyState>
        </Card>
      )
    }

    return (
      <Card>
        <div style={{ padding: '16px' }}>
          <Filters
            queryValue={searchQuery}
            filters={[
              {
                key: 'availability',
                label: 'Availability',
                filter: (
                  <ChoiceList
                    title="Availability"
                    titleHidden
                    choices={[
                      { label: 'In Stock', value: 'IN_STOCK' },
                      { label: 'Out of Stock', value: 'OUT_OF_STOCK' }
                    ]}
                    selected={availabilityFilter}
                    onChange={setAvailabilityFilter}
                    allowMultiple
                  />
                ),
                shortcut: true
              }
            ]}
            appliedFilters={[]}
            onQueryChange={setSearchQuery}
            onQueryClear={() => setSearchQuery('')}
            onClearAll={() => {
              setSearchQuery('')
              setAvailabilityFilter([])
            }}
          />
        </div>

        <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5' }}>
          <Stack alignment="center">
            <Stack.Item fill>
              <Text variant="bodyMd" as="p" color="subdued">
                Showing {sortedProducts.length} of {products.length} products
              </Text>
            </Stack.Item>
            <Select
              label="Sort by"
              labelInline
              options={[
                { label: 'Recently synced', value: 'recent' },
                { label: 'Most views', value: 'views' },
                { label: 'Most clicks', value: 'clicks' },
                { label: 'Most conversions', value: 'conversions' },
                { label: 'Price: High to Low', value: 'price-high' },
                { label: 'Price: Low to High', value: 'price-low' }
              ]}
              value={sortBy}
              onChange={setSortBy}
            />
          </Stack>
        </div>

        <ResourceList
          resourceName={{ singular: 'product', plural: 'products' }}
          items={sortedProducts}
          renderItem={(product) => {
            const { id, title, description, price, currency, availability, imageUrl, viewCount, clickCount, conversionCount } = product
            const media = (
              <Thumbnail
                source={imageUrl || 'https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png'}
                alt={title}
                size="medium"
              />
            )

            const availabilityBadge = availability === 'IN_STOCK'
              ? <Badge status="success">In Stock</Badge>
              : <Badge status="critical">Out of Stock</Badge>

            return (
              <ResourceItem
                id={id}
                media={media}
                accessibilityLabel={`View details for ${title}`}
              >
                <Stack vertical spacing="extraTight">
                  <Stack alignment="center">
                    <Stack.Item fill>
                      <Text variant="bodyMd" fontWeight="bold" as="h3">
                        {title}
                      </Text>
                    </Stack.Item>
                    {availabilityBadge}
                  </Stack>

                  {description && (
                    <Text variant="bodySm" as="p" color="subdued">
                      {description.substring(0, 100)}{description.length > 100 ? '...' : ''}
                    </Text>
                  )}

                  <Stack spacing="loose">
                    <Text variant="bodyMd" as="p">
                      <strong>Price:</strong> {currency} {price}
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      👁️ {viewCount} views
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      🖱️ {clickCount} clicks
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      ✅ {conversionCount} conversions
                    </Text>
                  </Stack>
                </Stack>
              </ResourceItem>
            )
          }}
        />
      </Card>
    )
  }

  // Sync History Tab Content
  const renderSyncHistoryTab = () => {
    if (loading) {
      return (
        <Card>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Spinner size="large" />
          </div>
        </Card>
      )
    }

    if (syncHistory.length === 0) {
      return (
        <Card>
          <EmptyState
            heading="No sync history"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Sync history will appear here once you run your first sync.</p>
          </EmptyState>
        </Card>
      )
    }

    const rows = syncHistory.map((sync) => {
      const statusBadge = sync.status === 'COMPLETED'
        ? <Badge status="success" icon={CircleTickMajor}>Completed</Badge>
        : sync.status === 'FAILED'
        ? <Badge status="critical" icon={CircleAlertMajor}>Failed</Badge>
        : sync.status === 'PARTIAL'
        ? <Badge status="warning">Partial</Badge>
        : <Badge status="info" icon={ClockMinor}>In Progress</Badge>

      const duration = sync.durationMs
        ? `${(sync.durationMs / 1000).toFixed(1)}s`
        : '-'

      return [
        new Date(sync.startedAt).toLocaleString(),
        <Badge>{sync.syncType}</Badge>,
        statusBadge,
        sync.productsTotal,
        sync.productsAdded,
        sync.productsUpdated,
        sync.productsFailed,
        duration
      ]
    })

    return (
      <Card>
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'text']}
          headings={['Started At', 'Type', 'Status', 'Total', 'Added', 'Updated', 'Failed', 'Duration']}
          rows={rows}
        />
      </Card>
    )
  }

  // Analytics Tab Content
  const renderAnalyticsTab = () => {
    const totalViews = products.reduce((sum, p) => sum + p.viewCount, 0)
    const totalClicks = products.reduce((sum, p) => sum + p.clickCount, 0)
    const totalConversions = products.reduce((sum, p) => sum + p.conversionCount, 0)
    const clickThroughRate = totalViews > 0 ? ((totalClicks / totalViews) * 100).toFixed(2) : '0.00'
    const conversionRate = totalClicks > 0 ? ((totalConversions / totalClicks) * 100).toFixed(2) : '0.00'

    // Top products
    const topByViews = [...products].sort((a, b) => b.viewCount - a.viewCount).slice(0, 5)
    const topByConversions = [...products].sort((a, b) => b.conversionCount - a.conversionCount).slice(0, 5)

    return (
      <Layout>
        <Layout.Section>
          <Card title="Performance Metrics">
            <div style={{ padding: '16px' }}>
              <Stack vertical spacing="loose">
                <Stack distribution="fillEvenly">
                  <div style={{ textAlign: 'center' }}>
                    <Text variant="heading2xl" as="h2">{totalViews}</Text>
                    <Text variant="bodyMd" as="p" color="subdued">Total Views</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text variant="heading2xl" as="h2">{totalClicks}</Text>
                    <Text variant="bodyMd" as="p" color="subdued">Total Clicks</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text variant="heading2xl" as="h2">{totalConversions}</Text>
                    <Text variant="bodyMd" as="p" color="subdued">Total Conversions</Text>
                  </div>
                </Stack>

                <Stack distribution="fillEvenly">
                  <div style={{ textAlign: 'center' }}>
                    <Text variant="headingXl" as="h3">{clickThroughRate}%</Text>
                    <Text variant="bodyMd" as="p" color="subdued">Click-Through Rate</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text variant="headingXl" as="h3">{conversionRate}%</Text>
                    <Text variant="bodyMd" as="p" color="subdued">Conversion Rate</Text>
                  </div>
                </Stack>
              </Stack>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section oneHalf>
          <Card title="Top Products by Views">
            {topByViews.length === 0 ? (
              <div style={{ padding: '16px' }}>
                <Text variant="bodyMd" as="p" color="subdued">No view data yet</Text>
              </div>
            ) : (
              <ResourceList
                resourceName={{ singular: 'product', plural: 'products' }}
                items={topByViews}
                renderItem={(product) => (
                  <ResourceItem
                    id={product.id}
                    media={
                      <Thumbnail
                        source={product.imageUrl || ''}
                        alt={product.title}
                        size="small"
                      />
                    }
                  >
                    <Stack alignment="center">
                      <Stack.Item fill>
                        <Text variant="bodyMd" as="p">{product.title}</Text>
                      </Stack.Item>
                      <Badge>{product.viewCount} views</Badge>
                    </Stack>
                  </ResourceItem>
                )}
              />
            )}
          </Card>
        </Layout.Section>

        <Layout.Section oneHalf>
          <Card title="Top Products by Conversions">
            {topByConversions.length === 0 ? (
              <div style={{ padding: '16px' }}>
                <Text variant="bodyMd" as="p" color="subdued">No conversion data yet</Text>
              </div>
            ) : (
              <ResourceList
                resourceName={{ singular: 'product', plural: 'products' }}
                items={topByConversions}
                renderItem={(product) => (
                  <ResourceItem
                    id={product.id}
                    media={
                      <Thumbnail
                        source={product.imageUrl || ''}
                        alt={product.title}
                        size="small"
                      />
                    }
                  >
                    <Stack alignment="center">
                      <Stack.Item fill>
                        <Text variant="bodyMd" as="p">{product.title}</Text>
                      </Stack.Item>
                      <Badge status="success">{product.conversionCount} conversions</Badge>
                    </Stack>
                  </ResourceItem>
                )}
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>
    )
  }

  return (
    <Page
      title="Catalog Management"
      primaryAction={{
        content: 'Sync Products',
        icon: RefreshMajor,
        onAction: () => setShowSyncModal(true),
        loading: syncing
      }}
      secondaryActions={[
        {
          content: 'Refresh',
          onAction: fetchCatalogData,
          icon: RefreshMajor
        }
      ]}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner status="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        {success && (
          <Layout.Section>
            <Banner status="success" onDismiss={() => setSuccess(null)}>
              <p>{success}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Sync Status Banner */}
        {syncStatus && (
          <Layout.Section>
            <Card>
              <div style={{ padding: '16px' }}>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <Stack.Item fill>
                      <Text variant="headingMd" as="h2">Sync Status</Text>
                    </Stack.Item>
                    {syncStatus.status === 'COMPLETED' && (
                      <Badge status="success" icon={CircleTickMajor}>Synced</Badge>
                    )}
                    {syncStatus.status === 'FAILED' && (
                      <Badge status="critical" icon={CircleAlertMajor}>Failed</Badge>
                    )}
                    {syncStatus.status === 'IN_PROGRESS' && (
                      <Badge status="info" icon={ClockMinor}>Syncing...</Badge>
                    )}
                  </Stack>

                  {syncStatus.lastSyncedAt && (
                    <Text variant="bodyMd" as="p" color="subdued">
                      Last synced: {new Date(syncStatus.lastSyncedAt).toLocaleString()}
                    </Text>
                  )}

                  {syncStatus.statistics && (
                    <Stack spacing="loose">
                      <Text variant="bodyMd" as="p">
                        <strong>{syncStatus.statistics.total}</strong> total products
                      </Text>
                      <Text variant="bodyMd" as="p" color="success">
                        <strong>{syncStatus.statistics.added}</strong> added
                      </Text>
                      <Text variant="bodyMd" as="p" color="warning">
                        <strong>{syncStatus.statistics.updated}</strong> updated
                      </Text>
                      {syncStatus.statistics.failed > 0 && (
                        <Text variant="bodyMd" as="p" color="critical">
                          <strong>{syncStatus.statistics.failed}</strong> failed
                        </Text>
                      )}
                    </Stack>
                  )}
                </Stack>
              </div>
            </Card>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
            <div style={{ padding: '16px' }}>
              {selectedTab === 0 && renderProductsTab()}
              {selectedTab === 1 && renderSyncHistoryTab()}
              {selectedTab === 2 && renderAnalyticsTab()}
            </div>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Sync Modal */}
      <Modal
        open={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        title="Sync Catalog Products"
        primaryAction={{
          content: 'Start Sync',
          onAction: handleSync,
          loading: syncing
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowSyncModal(false)
          }
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="loose">
            <TextContainer>
              <p>Choose the type of sync you want to perform:</p>
            </TextContainer>

            <Select
              label="Sync Type"
              options={[
                { label: 'Incremental Sync (Recommended)', value: 'incremental' },
                { label: 'Full Sync', value: 'full' }
              ]}
              value={syncType}
              onChange={(value) => setSyncType(value as 'full' | 'incremental')}
              helpText={
                syncType === 'full'
                  ? 'Full sync will fetch all products from Shopify and update the entire catalog. This may take several minutes.'
                  : 'Incremental sync will only update products that have changed since the last sync. This is faster and recommended for regular syncs.'
              }
            />

            {syncType === 'full' && (
              <Banner status="warning">
                <p>Full sync may take several minutes depending on the number of products. The sync will run in the background.</p>
              </Banner>
            )}
          </Stack>
        </Modal.Section>
      </Modal>
    </Page>
  )
}
